import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  loadStripe,
  type Stripe,
  type StripeCardCvcElement,
  type StripeCardExpiryElement,
  type StripeCardNumberElement,
} from '@stripe/stripe-js';
import { useStripeAccount } from './useStripeAccount';
import { useStripeFieldsStyle } from './useStripeFieldsStyle';

const CARD_PLACEHOLDER = '0000 0000 0000 0000';
const EXPIRATION_PLACEHOLDER = 'MM/YY';
const CVC_PLACEHOLDER = 'CVV';

export const useStripeCard = () => {
  const { stripePk } = useStripeAccount();
  const stripeFieldsStyle = useStripeFieldsStyle();

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [load, setLoad] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const card = useRef<StripeCardNumberElement | null>(null);
  const expiration = useRef<StripeCardExpiryElement | null>(null);
  const cvc = useRef<StripeCardCvcElement | null>(null);
  const cardholder = useRef<HTMLInputElement | null>(null);

  const clearFields = () => {
    if (cardholder.current) {
      cardholder.current.value = '';
    }
    [card.current, expiration.current, cvc.current].forEach((field) => field?.clear());
  };

  useEffect(() => {
    if (stripePk) {
      setStripePromise(loadStripe(stripePk, { locale: 'en' }));
    }
  }, [stripePk]);

  useLayoutEffect(() => {
    stripePromise?.then((stripe) => {
      if (!stripe) return;
      const elements = stripe.elements();
      card.current = elements.create('cardNumber', {
        style: stripeFieldsStyle,
        placeholder: CARD_PLACEHOLDER,
      });
      expiration.current = elements.create('cardExpiry', {
        style: stripeFieldsStyle,
        placeholder: EXPIRATION_PLACEHOLDER,
      });
      cvc.current = elements.create('cardCvc', {
        style: stripeFieldsStyle,
        placeholder: CVC_PLACEHOLDER,
      });
      setStripeInstance(stripe);
      setLoad(true);
    });
    // Elements are created once per Stripe instance; recreating them on style change would drop the mounted fields
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripePromise]);

  return {
    stripePromise,
    stripeInstance,
    load,
    isFormValid,
    setIsFormValid,
    card,
    expiration,
    cvc,
    cardholder,
    clearFields,
  };
};
